import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { verifyAuth, refreshToken } from '../../../utils/authHelper';
import { isCustomDomain as isCustomDomainCheck } from '../../../utils/domainUtils';

const COMPANY_DISPLAY_NAME_KEY = 'cachedCompanyDisplayName';
const AUTH_TRANSITIONING_KEY = 'authTransitioning';

const clearAuthTransitionFlags = () => {
  try {
    sessionStorage.removeItem(AUTH_TRANSITIONING_KEY);
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') === 'domain_redirect') {
      params.delete('from');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  } catch (e) { /* ignore */ }
};

const shouldShowAuthLoaderDuringLoad = () => {
  try {
    if (sessionStorage.getItem(AUTH_TRANSITIONING_KEY) === '1') return true;
    if (new URLSearchParams(window.location.search).get('from') === 'domain_redirect') return true;
  } catch (e) { /* ignore */ }
  return false;
};

const persistCompanyDisplayName = (userData) => {
  try {
    const displayName = userData?.company?.nickname || userData?.company?.name;
    if (displayName) {
      localStorage.setItem(COMPANY_DISPLAY_NAME_KEY, displayName);
    }
  } catch (error) {
    console.warn('Unable to persist company display name', error);
  }
};

const getInitialCachedUser = () => {
  try {
    const savedUser = localStorage.getItem('user');
    if (savedUser && savedUser !== 'undefined') {
      return JSON.parse(savedUser);
    }
    return null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    localStorage.removeItem('user');
    return null;
  }
};

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [user, setUser] = useState(getInitialCachedUser);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [domainError, setDomainError] = useState(false);
  const isCheckingAuth = useRef(false);
  const refreshIntervalRef = useRef(null);
  const lastUserDataRef = useRef(null);
  const userDataHashRef = useRef(null);
  const orgDataFetched = useRef(false);
  const initialAuthCheckCompleted = useRef(false);

  // Helper function to create a stable hash of user data
  const createUserDataHash = useCallback((userData) => {
    if (!userData) return null;
    
    // Create a stable hash from key user properties (include profileImage for avatar updates)
    const keyData = {
      id: userData.id,
      companyId: userData.companyId,
      subscriptionPlan: userData.company?.subscriptionPlan,
      isOnboarded: userData.isOnboarded,
      firstName: userData.firstName,
      lastName: userData.lastName,
      preferredName: userData.preferredName,
      email: userData.email,
      companyName: userData.company?.name,
      companyNickname: userData.company?.nickname,
      companyLocation: userData.company?.location,
      profileImage: userData.profileImage?.url ?? userData.profileImage?.key ?? null,
      defaultChatModelKey: userData.preferences?.defaultChatModelKey ?? null,
    };
    
    return JSON.stringify(keyData);
  }, []);

  // Helper function to update user data only if it changed
  const updateUserDataIfChanged = useCallback((newUserData) => {
    const newHash = createUserDataHash(newUserData);
    
    if (newHash !== userDataHashRef.current) {
      console.log('📝 User data changed, updating state');
      userDataHashRef.current = newHash;
      lastUserDataRef.current = newUserData;
      setUser(newUserData);
      localStorage.setItem('user', JSON.stringify(newUserData));
      persistCompanyDisplayName(newUserData);
      return true;
    }
    
    console.log('🔄 User data unchanged, skipping state update');
    return false;
  }, [createUserDataHash]);

  const isCustomDomain = useCallback(() => isCustomDomainCheck(), []);

  // Handle redirect to main domain
  const redirectToMainDomain = useCallback(() => {
    const currentPath = window.location.pathname;
    const mainDomain = 'https://midus.co';
    window.location.href = `${mainDomain}${currentPath}`;
  }, []);

  // Check if the current domain is valid
  const checkDomainValidity = useCallback(async () => {
    if (!isCustomDomain()) return true;
    
    try {
      // Try to get the hostname
      const hostname = window.location.hostname;
      
      // Check if we're using an IP address
      const isIPAddress = /^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::(\d{1,5}))?$/.test(hostname);
      
      // If it's an IP address, use the special endpoint
      if (isIPAddress) {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/domains/check-ip/${hostname}`, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        return response.data && response.data.valid !== false;
      }
      
      // Get the base domain by removing the 'ai.' prefix if it exists
      let baseDomain = hostname;
      if (hostname.startsWith('ai.')) {
        baseDomain = hostname.substring(3);
      }
      
      // Check domain validity with the server
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/domains/check-validity/${baseDomain}`, {
        withCredentials: true,
        // Add specific headers to help with CORS
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.data && response.data.valid === false) {
        console.error('Custom domain is no longer valid');
        setDomainError(true);
        return false;
      }
      
      return true;
    } catch (error) {
      // If this is an IP address like 10.0.0.223, always consider it valid
      const hostname = window.location.hostname;
      if (
        hostname.startsWith('10.') || 
        hostname.startsWith('192.168.') || 
        hostname.startsWith('127.')
      ) {
        console.log('Local IP detected, skipping validation');
        return true;
      }
      
      console.error('Error checking domain validity:', error);
      
      // Handle specific domain endpoint errors:
      // 1. Direct HTTP errors (404, 403)
      // 2. Network errors (which may include CORS issues)
      // 3. ERR_FAILED errors (which often mean the domain is invalid)
      
      // Any error specifically from the domain endpoint should trigger domain error 
      // since domain endpoints are critical to domain validation
      if (
        // Specific status codes
        (error.response && (error.response.status === 404 || error.response.status === 403)) ||
        // Network errors
        (error.code === 'ERR_NETWORK') ||
        // CORS errors often manifest as network errors
        (error.message && error.message.includes('Network Error')) ||
        // Other failed requests
        (error.message && error.message.includes('ERR_FAILED'))
      ) {
        console.log('Domain validation failed:', error.message || error.code);
        setDomainError(true);
        return false;
      }
      
      // For any other types of errors (timeouts, 500s not related to domain), 
      // don't trigger domain error but still log
      console.log('Non-domain related API error:', error.message || error.code);
      return true;
    }
  }, [isCustomDomain]);

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole'); // 🆕 Clear user role for SuperBucket access
    localStorage.removeItem(COMPANY_DISPLAY_NAME_KEY);
    localStorage.removeItem('authState');
    localStorage.removeItem('tempAuthSession');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('tempAuthSession');
    lastUserDataRef.current = null;
    userDataHashRef.current = null;
    orgDataFetched.current = false;
    setUser(null);
    setIsAuthenticated(false);
    setIsOnboarded(false);
    setIsLoading(false);
    clearAuthTransitionFlags();
  }, []);

  // Helper to check if current token is temporary (requires 2FA)
  const hasTemporaryToken = () => {
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (!token) return false;
      
      // Decode the token to check for tempAuth flag
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.tempAuth === true;
    } catch (error) {
      return false;
    }
  };

  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth.current) {
      console.log('🔄 Auth check already in progress, skipping...');
      return lastUserDataRef.current;
    }
    
    isCheckingAuth.current = true;
    
    try {
      // First, check if the domain is valid if we're on a custom domain
      const isDomainValid = await checkDomainValidity();
      if (!isDomainValid) {
        clearAuthData();
        return null;
      }
      
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        const isValid = await verifyAuth();
        if (isValid) {
          try {
            console.log('🔐 Auth verified, fetching user data...');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const userData = response.data;
            
            console.log('🔍 USER DATA FROM /auth/me:', {
              needsOrganization: userData.needsOrganization,
              isOnboarded: userData.isOnboarded,
              companyId: userData.companyId,
              hasCompany: !!userData.company,
              currentPath: window.location.pathname
            });
            
            // Check if user's organization was deleted and they need to create a new one
            if (userData.needsOrganization && window.location.pathname !== '/select-organization') {
              console.log('⚠️ User has no organization - redirecting to /select-organization');
              sessionStorage.setItem('organizationDeleted', 'true');
              // Just redirect - user stays logged in
              window.location.href = '/select-organization';
              return null;
            } else if (userData.needsOrganization) {
              console.log('ℹ️ User needs organization but already on org selector page');
              // Set minimal data to prevent errors
              userData.company = userData.company || { subscriptionPlan: 'none' };
              updateUserDataIfChanged(userData);
              setIsAuthenticated(true);
              setIsOnboarded(true);
              setIsLoading(false);
              return userData;
            }
            
            // Check if this is a temporary token requiring 2FA
            const isTempToken = hasTemporaryToken();
            if (isTempToken) {
              console.log('🔐 Temporary token detected, keeping user in 2FA flow');
              const tempSessionActive = sessionStorage.getItem('tempAuthSession') === 'true';
              if (!tempSessionActive) {
                console.log('⚠️ Temp auth without active session, clearing');
                clearAuthData();
                return null;
              }
              // Set minimal defaults for temporary token users
              userData.company = userData.company || {};
              userData.company.subscriptionPlan = 'none';
              
              // Update user state with temporary token user
              updateUserDataIfChanged(userData);
              setIsAuthenticated(false);
              setIsOnboarded(false);
              setIsLoading(false);
              return userData;
            }
            
            // Always fetch organization information as part of auth flow (only for full tokens)
            if (!orgDataFetched.current) {
              console.log('👥 Loading organization data as part of auth...');
              orgDataFetched.current = true;
              
              try {
                // 🛠️ Prepare headers with test org override support
                const headers = {
                  Authorization: `Bearer ${token}`
                };
                
                // Add test org header if in test mode
                if (process.env.NODE_ENV === 'development') {
                  const testOrgMode = localStorage.getItem('devTestOrgMode');
                  const testOrgId = localStorage.getItem('devTestOrgId');
                  
                  if (testOrgMode === 'true' && testOrgId) {
                    headers['X-Test-Org-Id'] = testOrgId;
                    console.log('🧪 Adding test org header to organization API call:', testOrgId);
                  }
                }
                
                const orgResponse = await axios.get(`${process.env.REACT_APP_API_URL}/organization`, {
                  headers
                });
                
                // Get organization info
                if (orgResponse.data.hasOrganization) {
                  const orgData = orgResponse.data.organization;
                  console.log('✅ Organization data loaded:', orgData);
                  
                  // Store organization ID in user data
                  userData.companyId = orgData.id;
                  
                  // 🆕 Store user role in localStorage for SuperBucket access
                  if (orgData.userRole) {
                    localStorage.setItem('userRole', orgData.userRole);
                    console.log('🔑 User role stored in localStorage:', orgData.userRole);
                  }
                  
                  // Always fetch company details to get subscription plan
                  console.log('💳 Loading company subscription data...');
                  try {
                    const companyResponse = await axios.get(`${process.env.REACT_APP_API_URL}/companies/${orgData.id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    console.log('Company API response:', companyResponse.data);
                    
                    if (companyResponse.data.company) {
                      // Add subscription plan info to user data
                      userData.company = userData.company || {};
                      userData.company.subscriptionPlan = companyResponse.data.company.subscriptionPlan || 'none';
                      userData.company.subscriptionStatus = companyResponse.data.company.subscriptionStatus || 'none';
                      userData.company.id = orgData.id;
                      userData.company.name = orgData.name || userData.company.name;
                      userData.company.nickname = companyResponse.data.company.nickname || '';
                      userData.company.location = companyResponse.data.company.location || '';
                      console.log(`✅ Subscription plan loaded: ${userData.company.subscriptionPlan}`);
                    } else {
                      console.warn('❌ Company data structure is incorrect:', companyResponse.data);
                      // Set default values to prevent undefined errors
                      userData.company = userData.company || {};
                      userData.company.subscriptionPlan = 'none';
                      userData.company.subscriptionStatus = 'none';
                      userData.company.id = orgData.id;
                    }
                  } catch (companyError) {
                    console.error('❌ Error fetching company subscription info:', companyError.response?.data || companyError.message);
                    
                    // Set default values to prevent undefined errors in routing logic
                    userData.company = userData.company || {};
                    userData.company.subscriptionPlan = 'none';
                    userData.company.id = orgData.id;
                  }
                } else {
                  console.log('ℹ️ User has no organization');
                  // Ensure company object exists with default values
                  userData.company = userData.company || {};
                  userData.company.subscriptionPlan = userData.company.subscriptionPlan || 'none';
                }
              } catch (orgError) {
                console.error('❌ Error fetching organization info:', orgError.response?.data || orgError.message);
                // Set default values to prevent routing issues
                userData.company = userData.company || {};
                userData.company.subscriptionPlan = userData.company.subscriptionPlan || 'none';
              }
            } else {
              // Use cached organization data
              console.log('🔄 Using cached organization data');
              if (lastUserDataRef.current?.companyId) {
                userData.companyId = lastUserDataRef.current.companyId;
                userData.company = lastUserDataRef.current.company || {};
              } else {
                userData.company = userData.company || {};
                userData.company.subscriptionPlan = userData.company.subscriptionPlan || 'none';
              }
            }
            
            console.log('🎯 Final user data with org/company info:', {
              userId: userData.id || userData._id,
              companyId: userData.companyId,
              subscriptionPlan: userData.company?.subscriptionPlan,
              isOnboarded: userData.isOnboarded
            });
            
            // Only update user data if it actually changed
            const userDataChanged = updateUserDataIfChanged(userData);
            
            if (userDataChanged || !isAuthenticated) {
              setIsAuthenticated(true);
              setIsOnboarded(userData.isOnboarded);
            }
            
            setIsLoading(false);
            return userData;
          } catch (error) {
            console.error('Error fetching fresh user data:', error);
            clearAuthData();
          }
        } else {
          clearAuthData();
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error in checkAuth:', error);
      clearAuthData();
    } finally {
      isCheckingAuth.current = false;
      clearAuthTransitionFlags();
    }
    return null;
  }, [clearAuthData, checkDomainValidity, updateUserDataIfChanged, isAuthenticated]);

  const handleAuthentication = useCallback(async (userData, onboarded = false) => {
    try {
      // Set loading state while we fetch complete user data
      setIsLoading(true);
      
      // Store the token first
      if (userData.token) {
        localStorage.setItem('token', userData.token);
      }
      
      // Fetch complete user data including subscription info before setting authenticated state
      const token = userData.token || localStorage.getItem('token') || sessionStorage.getItem('token');
      const sessionToken = userData.token || sessionStorage.getItem('token');
      if (token && userData.user) {
        try {
          console.log('🔄 Loading complete user data including subscription info...');
          
          // Start with the user data from login
          let completeUserData = { ...userData.user };
          
          // Check if user's organization was deleted and they need to create a new one
          if (completeUserData.needsOrganization && window.location.pathname !== '/select-organization') {
            console.log('⚠️ User has no organization - redirecting to /select-organization');
            sessionStorage.setItem('organizationDeleted', 'true');
            // Just redirect - user stays logged in
            window.location.href = '/select-organization';
            return;
          } else if (completeUserData.needsOrganization) {
            console.log('ℹ️ User needs organization but already on org selector page');
            // Set minimal data to prevent errors
            completeUserData.company = completeUserData.company || { subscriptionPlan: 'none' };
            updateUserDataIfChanged(completeUserData);
            setIsAuthenticated(true);
            setIsOnboarded(true);
            setIsLoading(false);
            return;
          }
          
          // Check if this is a temporary token requiring 2FA
          const isTempToken = hasTemporaryToken();
          if (isTempToken) {
            console.log('🔐 Temporary token detected in authentication, keeping user unauthenticated until 2FA completes');
            sessionStorage.setItem('token', sessionToken || token);
            sessionStorage.setItem('tempAuthSession', 'true');
            localStorage.removeItem('token');
            // Set minimal defaults for temporary token users
            completeUserData.company = completeUserData.company || {};
            completeUserData.company.subscriptionPlan = 'none';
            
            // Set auth state with temporary token user
            updateUserDataIfChanged(completeUserData);
            setIsAuthenticated(false);
            setIsOnboarded(false);
            setIsLoading(false);
            return;
          }
          
          // Always fetch organization information as part of auth flow (only for full tokens)
          console.log('👥 Loading organization data as part of auth...');
          try {
            // 🛠️ Prepare headers with test org override support
            const headers = {
              Authorization: `Bearer ${token}`
            };
            
            // Add test org header if in test mode
            if (process.env.NODE_ENV === 'development') {
              const testOrgMode = localStorage.getItem('devTestOrgMode');
              const testOrgId = localStorage.getItem('devTestOrgId');
              
              if (testOrgMode === 'true' && testOrgId) {
                headers['X-Test-Org-Id'] = testOrgId;
                console.log('🧪 Adding test org header to organization API call:', testOrgId);
              }
            }
            
            const orgResponse = await axios.get(`${process.env.REACT_APP_API_URL}/organization`, {
              headers
            });
            
            // Get organization info
            if (orgResponse.data.hasOrganization) {
              const orgData = orgResponse.data.organization;
              console.log('✅ Organization data loaded:', orgData);
              
              // Store organization ID in user data
              completeUserData.companyId = orgData.id;
              
              // 🆕 Store user role in localStorage for SuperBucket access
              if (orgData.userRole) {
                localStorage.setItem('userRole', orgData.userRole);
                console.log('🔑 User role stored in localStorage:', orgData.userRole);
              }
              
              // Always fetch company details to get subscription plan
              console.log('💳 Loading company subscription data...');
              try {
                const companyResponse = await axios.get(`${process.env.REACT_APP_API_URL}/companies/${orgData.id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                console.log('Company API response:', companyResponse.data);
                
                if (companyResponse.data.company) {
                  // Add subscription plan info to user data
                  completeUserData.company = completeUserData.company || {};
                  completeUserData.company.subscriptionPlan = companyResponse.data.company.subscriptionPlan || 'none';
                  completeUserData.company.subscriptionStatus = companyResponse.data.company.subscriptionStatus || 'none';
                  completeUserData.company.id = orgData.id;
                  completeUserData.company.name = orgData.name || completeUserData.company.name;
                  completeUserData.company.nickname = companyResponse.data.company.nickname || '';
                  completeUserData.company.location = companyResponse.data.company.location || '';
                  console.log(`✅ Subscription plan loaded: ${completeUserData.company.subscriptionPlan}`);
                } else {
                  console.warn('❌ Company data structure is incorrect:', companyResponse.data);
                  // Set default values to prevent undefined errors
                  completeUserData.company = completeUserData.company || {};
                  completeUserData.company.subscriptionPlan = 'none';
                  completeUserData.company.id = orgData.id;
                }
              } catch (companyError) {
                console.error('❌ Error fetching company subscription info:', companyError.response?.data || companyError.message);
                
                // Set default values to prevent undefined errors in routing logic
                completeUserData.company = completeUserData.company || {};
                completeUserData.company.subscriptionPlan = 'none';
                completeUserData.company.id = orgData.id;
              }
            } else {
              console.log('ℹ️ User has no organization');
              // Ensure company object exists with default values
              completeUserData.company = completeUserData.company || {};
              completeUserData.company.subscriptionPlan = completeUserData.company.subscriptionPlan || 'none';
            }
          } catch (orgError) {
            console.error('❌ Error fetching organization info:', orgError.response?.data || orgError.message);
            // Set default values to prevent routing issues
            completeUserData.company = completeUserData.company || {};
            completeUserData.company.subscriptionPlan = completeUserData.company.subscriptionPlan || 'none';
          }
          
          console.log('🎯 Complete user data with subscription info loaded:', {
            userId: completeUserData.id || completeUserData._id,
            companyId: completeUserData.companyId,
            subscriptionPlan: completeUserData.company?.subscriptionPlan,
            isOnboarded: completeUserData.isOnboarded
          });
          
          // Now set the authenticated state with complete data using the safe update function
          updateUserDataIfChanged(completeUserData);
          setIsAuthenticated(true);
          setIsOnboarded(onboarded);
        } catch (error) {
          console.error('❌ Error loading complete user data:', error);
          // Fall back to setting auth state with original data
          updateUserDataIfChanged(userData.user);
          setIsAuthenticated(true);
          setIsOnboarded(onboarded);
        }
      } else {
        // No token or user data, fall back to original behavior
        updateUserDataIfChanged(userData.user);
        setIsAuthenticated(true);
        setIsOnboarded(onboarded);
      }
    } catch (error) {
      console.error('❌ Error in handleAuthentication:', error);
      // Fall back to original behavior
      updateUserDataIfChanged(userData.user);
      setIsAuthenticated(true);
      setIsOnboarded(onboarded);
    } finally {
      setIsLoading(false);
    }
  }, [updateUserDataIfChanged]);

  useEffect(() => {
    if (initialAuthCheckCompleted.current) {
      return;
    }

    let mounted = true;
    
    const performAuthCheck = async () => {
      if (mounted && !isCheckingAuth.current) {
        // For temporary tokens, we don't need to keep checking auth repeatedly
        const tempToken = hasTemporaryToken();
        if (tempToken && isAuthenticated) {
          console.log('🔐 Skipping auth check for temporary token user');
          initialAuthCheckCompleted.current = true;
          return;
        }
        
        try {
          await checkAuth();
        } finally {
          initialAuthCheckCompleted.current = true;
        }
      }
    };
    
    performAuthCheck();
    
    return () => {
      mounted = false;
    };
  }, [checkAuth, isAuthenticated]);

  // Add automatic token refresh every hour for active users (but not for temporary tokens)
  useEffect(() => {
    if (isAuthenticated && !isLoading && !hasTemporaryToken()) {
      // Set up periodic token refresh every hour
      refreshIntervalRef.current = setInterval(async () => {
        try {
          console.log('🔄 Performing automatic token refresh...');
          await refreshToken();
          console.log('✅ Token auto-refreshed successfully');
        } catch (error) {
          console.error('❌ Automatic token refresh failed:', error);
          // If refresh fails, clear auth data instead of calling checkAuth
          clearAuthData();
        }
      }, 60 * 60 * 1000); // 1 hour in milliseconds

      // Clean up interval on unmount or when user is no longer authenticated
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval if user is not authenticated or has temporary token
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [isAuthenticated, isLoading, clearAuthData]);

  // Effect to handle domain errors
  useEffect(() => {
    if (domainError) {
      // No immediate redirect - DomainError component will handle the countdown and redirect
      console.log('Domain is invalid, showing error screen');
    }
  }, [domainError]);

  // Only show AuthLoader during logout or custom domain redirect, not on every load
  const shouldShowAuthLoader = isLoading && (shouldShowAuthLoaderDuringLoad() || isLoggingOut);

  // immediate: true = logout flow - set isLoggingOut + isLoading (no sessionStorage, avoids double loader on reload)
  // immediate: false = custom domain redirect - set sessionStorage + isLoading (new page shows loader)
  const setAuthTransitioning = useCallback((immediate = false) => {
    try {
      if (immediate) {
        setIsLoggingOut(true);
      } else {
        sessionStorage.setItem(AUTH_TRANSITIONING_KEY, '1');
      }
      setIsLoading(true);
    } catch (e) { /* ignore */ }
  }, []);

  return {
    isAuthenticated,
    isOnboarded,
    user,
    isLoading,
    shouldShowAuthLoader,
    isLoggingOut,
    setAuthTransitioning,
    checkAuth,
    handleAuthentication,
    setIsAuthenticated,
    setUser: updateUserDataIfChanged,
    setIsOnboarded,
    clearAuthData,
    domainError,
    redirectToMainDomain
  };
}; 